import { useRef, useState } from 'react';
import { useExcluirModeloPeca, useModelosPeca, useUploadModeloPeca } from '../hooks';
import { GRUPOS, TIPOS_PECA } from '../tiposPeca';
import type { ModeloPecaStatus } from '../types';

function fmtData(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch {
    return iso.slice(0, 10);
  }
}

/**
 * Tabela de modelos .docx por tipo de peça (metade esquerda da aba
 * "Peticionar com modelo"). Cada linha: tipo · status · slot de upload · excluir.
 * Os modelos contêm campos Jinja2/docxtpl ({{CAMPO}}, {% if fPN %}) e ficam no
 * Drive do escritório; o índice fica no banco (admin/gerente).
 */
export function ModelosTemplateTable() {
  const { data: status, isLoading } = useModelosPeca();
  const upload = useUploadModeloPeca();
  const excluir = useExcluirModeloPeca();
  const [erroKey, setErroKey] = useState<string | null>(null);
  const [erroMsg, setErroMsg] = useState<string>('');
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  function onPick(key: string, file: File | null) {
    setErroKey(null);
    setErroMsg('');
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      setErroKey(key);
      setErroMsg('O modelo deve ser um arquivo .docx');
      return;
    }
    upload.mutate({ key, file }, {
      onError: (e: unknown) => {
        setErroKey(key);
        setErroMsg(e instanceof Error ? e.message : 'Falha no upload');
      },
    });
  }

  const mods: Record<string, ModeloPecaStatus> = status ?? {};
  const comModelo = Object.values(mods).filter((m) => m.tem_modelo).length;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          📄 Modelos de petição
        </h2>
        <span className="text-[11px] text-slate-400">
          {comModelo}/{TIPOS_PECA.length} com modelo
        </span>
      </div>
      <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">
        Suba um modelo <code className="px-1 rounded bg-slate-100 dark:bg-slate-800">.docx</code> por
        tipo de peça, com campos <code className="px-1 rounded bg-slate-100 dark:bg-slate-800">{'{{CAMPO}}'}</code> e
        blocos <code className="px-1 rounded bg-slate-100 dark:bg-slate-800">{'{% if fPN %}'}</code>.
        Os arquivos ficam no Drive do escritório.
      </p>

      {isLoading ? (
        <p className="text-sm text-slate-400">Carregando…</p>
      ) : (
        <div className="space-y-5">
          {GRUPOS.map((grupo) => {
            const tipos = TIPOS_PECA.filter((t) => t.grupo === grupo);
            return (
              <div key={grupo}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                  {grupo}
                </div>
                <ul className="rounded-xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
                  {tipos.map((t) => {
                    const m = mods[t.key];
                    const tem = !!m?.tem_modelo;
                    const busy = upload.isPending && upload.variables?.key === t.key;
                    const del = excluir.isPending && excluir.variables === t.key;
                    return (
                      <li key={t.key} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-900/30">
                        <span className="flex-1 min-w-0">
                          <span className="block text-xs font-medium text-slate-700 dark:text-slate-200 truncate">
                            {t.rotulo}
                          </span>
                          {tem ? (
                            <span className="block text-[10px] text-emerald-600 dark:text-emerald-400 truncate">
                              ✓ {m!.nome ?? 'modelo.docx'}{m!.atualizado_em ? ` · ${fmtData(m!.atualizado_em)}` : ''}
                            </span>
                          ) : (
                            <span className="block text-[10px] text-slate-400">sem modelo</span>
                          )}
                          {erroKey === t.key && (
                            <span className="block text-[10px] text-rose-500">{erroMsg}</span>
                          )}
                        </span>
                        <input
                          ref={(el) => { inputs.current[t.key] = el; }}
                          type="file"
                          accept=".docx"
                          className="hidden"
                          onChange={(e) => onPick(t.key, e.target.files?.[0] ?? null)}
                        />
                        <button
                          onClick={() => inputs.current[t.key]?.click()}
                          disabled={busy}
                          className="px-2.5 py-1 text-[11px] rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 disabled:opacity-50 whitespace-nowrap"
                          title={tem ? 'Substituir o modelo' : 'Subir o modelo .docx'}
                        >
                          {busy ? '⏳' : tem ? '↻ Trocar' : '⬆ Subir'}
                        </button>
                        {tem && (
                          <button
                            onClick={() => excluir.mutate(t.key)}
                            disabled={del}
                            className="px-2 py-1 text-[11px] rounded-lg bg-slate-100 dark:bg-slate-800 text-rose-600 dark:text-rose-400 hover:bg-rose-50 disabled:opacity-50"
                            title="Remover o modelo"
                          >
                            {del ? '⏳' : '🗑'}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
